<template>
<!-- Start payment div -->
  <div class="o-paymentsDetails">
    <billing-address ref="billingAddress" v-if="showPaymentOptions"/>
    <!-- Start o-paymentsDetails--form div -->
    <div class="o-paymentsDetails--form" v-if="showPaymentOptions">
      <div class="o-paymentsDetails--form__title">Payment method*</div>
      <SavedMethods
        v-if="hasStoredPaymentMethods"
        ref="savedMethod"
        v-bind:paymentFlow="paymentFlow"
        v-on:selectPaymentMethod="selectSavedPaymentMethod"
      />
      <ToggleText
        v-if="hasStoredPaymentMethods"
        :faIconClass="showPaymentsMethod ? 'fas fa-minus' : 'fas fa-plus'"
        text="Add new payment method"
        :class="'o-paymentsDetails--form__toggle'"
        v-on:toggleEditable="togglePaymentsMethod"
      />
      <CreditCard
        ref="CreditCard"
        v-bind:clientInstance="clientInstance"
        v-on:openCreditCard="openCreditCard"
        v-if="deviceData && showPaymentsMethod"
      />
      <PayPal
        ref="PayPal"
        v-on:paypalAuthorized="paypalAuthorized"
        v-bind:clientInstance="clientInstance"
        v-on:openPayPal="openPayPal"
        v-if="deviceData && showPaymentsMethod"
      />
    </div>
    <div class="o-paymentsDetails--options">
      <hr v-if="showPaymentOptions"/>
      <div class="o-paymentsDetails--options__agreeMsg">
          By clicking the button below, you agree to Serato's
          <a class="a-Link--Blue" target="_blank" href="/legal/terms-of-sale" rel="noopener noreferrer">terms&nbsp;and&nbsp;conditions</a>
          and <a class="a-Link--Blue" target="_blank" href="/legal/serato-privacy-policy" rel="noopener noreferrer">privacy&nbsp;policy.</a>
      </div>
      <button
          id="o-paymentsDetails--options__submit"
          v-on:click.prevent="completeOrder"
          v-html="showPaymentOptions ? 'Pay now' : 'Confirm'"
          class="a-Button--Large a-Button--Orange">
      </button>
    </div>
    <!-- End o-paymentsDetails--form div -->
  </div>
<!-- End payment div -->
</template>

<script>
import { mapState } from 'vuex'
import Axios from 'axios'
import Braintree from 'braintree-web/client'
import DataCollector from 'braintree-web/data-collector'
import CreditCard from '../molecules/CreditCard.vue'
import PayPal from '../molecules/PayPal.vue'
import SavedMethods from '../molecules/SavedMethods.vue'
import BillingAddress from './BillingAddress.vue'
import ToggleText from '../atoms/ToggleText.vue'

export default {
  computed: mapState({
    paymentGatewayInfo: state => state.globalstate.paymentGatewayInfo,
    storedPaymentMethods: state => state.globalstate.paymentGatewayInfo.storedPaymentMethods,
    userId: state => state.user.id,
    items: state => state.cart.items,
    isProductSubscription: state => state.cart.isSubscription,
    taxAmount: state => state.cart.taxAmount,
    totalAmount: state => state.cart.totalAmount,
    gtmCheckoutName: state => state.globalstate.gtmCheckoutName,
    gtmProducts: state => state.cart.gtmProducts,
    couponCode: state => state.cart.couponCode,
    showPaymentOptions () {
      // Hiding payment options if the order amount is ZERO dollar and order is not for a subscription
      if (this.paymentGatewayInfo.clientToken) {
        if (this.isProductSubscription) {
          return true
        } else {
          return this.totalAmount > 0
        }
      }
      return false
    },
    hasStoredPaymentMethods () {
      if (this.storedPaymentMethods !== undefined && this.storedPaymentMethods.length > 0) {
        // a stored payment method exit so we hide the field to add a new one.
        this.showPaymentsMethod = false
        return true
      } else {
        // there is no stored payment method so we toggle the field to add a new one.
        this.showPaymentsMethod = true
        return false
      }
    }
  }),
  components: {
    BillingAddress,
    SavedMethods,
    CreditCard,
    PayPal,
    ToggleText
  },
  watch: {
    paymentGatewayInfo () {
      this.createBraintreeClient()
    },
    totalAmount () {
      this.createBraintreeClient()
    }
  },
  data () {
    return {
      showPaymentsMethod: false,
      clientInstance: '',
      deviceData: '',
      payMethodUsed: '',
      paymentFlow: 'vault',
      errorMessage: 'Oops – something went wrong. ' +
        'Please check your payment information or try a different card. ' +
        'If you’re still having issues, <a href="mailto:sales@serato.com">Contact Support</a><br> ' +
        'Note: the payment methods we currently accept are PayPal, Visa and Mastercard (credit and debit cards).'
    }
  },
  methods: {
    InvalidAddressException (message) {
      this.message = message
      this.name = 'InvalidAddress'
    },
    togglePaymentsMethod () {
      this.showPaymentsMethod = !this.showPaymentsMethod
    },
    paypalAuthorized (paypalNonce, flow) {
      if (paypalNonce) {
        this.paymentFlow = flow
        this.completeOrder()
      }
    },
    unSelectStoredPaymentMethod () {
      if (this.storedPaymentMethods) {
        this.$refs.savedMethod.selected = ''
      }
    },
    openCreditCard () {
      if (this.$refs.PayPal) this.$refs.PayPal.collapsePayPal()
      this.unSelectStoredPaymentMethod()
    },
    openPayPal () {
      if (this.$refs.CreditCard) this.$refs.CreditCard.collapseCreditCard()
      this.unSelectStoredPaymentMethod()
    },
    selectSavedPaymentMethod () {
      if (this.$refs.CreditCard) this.$refs.CreditCard.collapseCreditCard()
      if (this.$refs.PayPal) this.$refs.PayPal.collapsePayPal()
    },
    enablePayButton () {
      const element = document.getElementById('o-paymentsDetails--options__submit')
      element.classList.remove('a-Button--Disabled')
      element.disabled = false
    },
    disablePayButton () {
      const element = document.getElementById('o-paymentsDetails--options__submit')
      element.classList.add('a-Button--Disabled')
      element.disabled = true
    },
    scrollViewToFormError () {
      // scroll to the first error -- on VeeValidate error we add error-msg to the label
      this.$nextTick(() => {
        const elementsInErrors = document.getElementsByClassName('a-input__error-message')
        if (elementsInErrors && elementsInErrors.length > 0) {
          elementsInErrors[0].scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
        }
      })
    },
    async completeOrder () {
      const data = {}
      this.disablePayButton()
      try {
        if (this.showPaymentOptions) {
          const selector = document.querySelector('input[name="paymenttoken"]:checked')
          if (!selector) {
            throw new Error('No payments method selected')
          }
          const billingAddress = this.$refs.billingAddress
          const addressFormIsValid = await billingAddress.validateForm()
          if (!addressFormIsValid) {
            throw new this.InvalidAddressException('Address form is not valid.')
          }
          // we first check all the payments details so if we have a new card token problem
          // or user havent logged in into his paypal account
          // or he has tricked the browser and remove the token for his account
          data.deviceData = this.deviceData
          this.$payMethodUsed = selector.value
          switch (selector.value) {
            case 'creditcard': // New Credit Card
              data.nonce = await this.$refs.CreditCard.tokenizeCreditCard()
              break
            case 'paypal': // New Paypal
              data.nonce = this.$refs.PayPal.nonce
              data.isPaypal = true
              if (data.nonce === '') throw new Error(this.errorMessage)
              break
            default: // Stored Payment Method
              data.token = selector.value
              if (this.$refs.savedMethod.selected.type === 'PayPalAccount') {
                data.isPaypal = true
                this.$payMethodUsed = 'paypal'
              } else {
                this.$payMethodUsed = 'creditcard'
              }
              break
          }
          // processPayment only if there is no error on validating US PostCode or updating the address.
          const response = await billingAddress.saveAddress()
          if (
            response !== undefined && response !== null &&
            response.zipCodeError !== undefined &&
            response.zipCodeError !== ''
          ) {
            throw new this.InvalidAddressException('Error while updating your address.')
          }
        }
        this.processPayment(data)
      } catch (error) {
        // don't show any Message for invalid address, as the error forms are highligthed.
        if (error.name !== 'InvalidAddress') {
          this.$store.dispatch('globalstate/error', { error: this.errorMessage })
        }
      }
      this.scrollViewToFormError()
      this.enablePayButton()
    },
    async processPayment (data) {
      this.disablePayButton()
      let errorMessage = ''
      // if purchase is ZERO dollar and order is not for a subscription,
      // we complete the cart payment and don't need nonce or token
      // if showPaymentOptions we need either nonce or token.
      // Has it's a purchase for more than 0$ or a sub.
      if (!this.showPaymentOptions ||
        (
          this.showPaymentOptions && (
            data.nonce !== undefined ||
            data.token !== undefined
          )
        )
      ) {
        await Axios.post('/payment/create', data)
        .then((response) => {
          if (response.data !== null) {
            if (response.data.order) {
              if (this.$refs.CreditCard) {
                this.$refs.CreditCard.teardownHostedFields()
              }
              this.$router.push({ path: '/success' })
              this.gtmPayTrigger(response.data.order)
            } else {
              errorMessage = response.data.error
            }
          } else {
            errorMessage = this.errorMessage
          }
        })
        .catch((error) => {
          if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || error.response.status === 504) {
            errorMessage = 'Higher traffic is causing things to take longer than usual. ' +
              'Your payment may have been processed successfully, please check ' +
              '<a href="https://account.serato.com/#/products">' +
              'My Account</a> or your email – if you don’t see anything in the next 5 minutes, try again.'
          } else {
            errorMessage = this.errorMessage
          }
        })
        if (errorMessage !== '') {
          this.$store.dispatch('globalstate/error', { error: errorMessage })
        }
      }
      this.enablePayButton()
    },
    createBraintreeClient () {
      if (this.showPaymentOptions) {
        Braintree.create(
          { authorization: this.paymentGatewayInfo.clientToken },
          (clientErr, clientInstance) => {
            if (!clientErr) {
              this.clientInstance = clientInstance
              DataCollector.create({
                client: clientInstance,
                paypal: true,
                kount: false
              }, (err, dataCollectorInstance) => {
                if (!err) {
                  this.deviceData = dataCollectorInstance.deviceData
                } else {
                  // TODO: handle error
                }
              })
            } else {
              // TODO: handle error console.error('There was an error setting up the client instance.')
            }
          }
        )
      }
      return
    },
    gtmPayTrigger ($orderResponse) {
      this.$gtm.trackEvent({
        'event': 'eec-purchase',
        'checkout_type': this.gtmCheckoutName,
        'userId': this.userId,
        'payment_method': this.$payMethodUsed,
        'ecommerce': {
          'purchase': {
            'actionField': {
              'id': $orderResponse['transactionId'],          // Transaction ID. Required for purchases and refunds.
              'revenue': this.totalAmount,     // Total transaction value (incl. tax and shipping)
              'tax': this.taxAmount,
              'coupon': this.couponCode
            },
            'products': this.gtmProducts
          }
        }
      })
    }
  }
}

</script>



// WEBPACK FOOTER //
// PaymentsDetails.vue?07435b77